import React, { useContext, useState } from "react";
import ShapeFlowObject from "../../model/ShapeFlowObject";
import {
    SHAPES_BY_CATEGORY,
    SHAPE_CATEGORIES,
    ShapeCategory,
    ShapeDefinition,
} from "../../model/ShapeDefinitions";
import Popup from "reactjs-popup";
import { MachineContext } from "../fsm/useMachine";
import { ADD_MMO } from "../fsm/types";
import ShapeIcon from "../icons/ShapeIcon";
import { XCircle } from "react-feather";

/** Renders a small SVG preview of a shape definition */
const ShapeThumbnail: React.FC<{ def: ShapeDefinition; size: number }> = ({ def, size }) => {
    const pad = 4;
    const inner = size - pad * 2;

    let child: React.ReactNode;
    switch (def.renderType) {
        case "rect":
            child = <rect x={pad} y={pad} width={inner} height={inner} />;
            break;
        case "ellipse":
            child = <ellipse cx={size / 2} cy={size / 2} rx={inner / 2} ry={inner / 2} />;
            break;
        case "roundedRect": {
            const r = inner * 5 / 28;
            child = <rect x={pad} y={pad} width={inner} height={inner} rx={r} ry={r} />;
            break;
        }
        case "path": {
            if (def.pathString) {
                const scaled = def.pathString.replace(
                    /(\d+\.?\d*)\s+(\d+\.?\d*)/g,
                    (_m, x, y) => `${parseFloat(x) * inner + pad} ${parseFloat(y) * inner + pad}`
                );
                child = <path d={scaled} fillRule="evenodd" />;
            }
            break;
        }
        case "polygon":
        default: {
            const points = def.pointsArrayString
                .split(" ")
                .map(pair => {
                    const [x, y] = pair.split(",");
                    return `${parseFloat(x) * inner + pad},${parseFloat(y) * inner + pad}`;
                })
                .join(" ");
            child = <polygon points={points} />;
            break;
        }
    }

    return (
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
            <g className="shape_fill">{child}</g>
        </svg>
    );
};

export const ShapeMenu = () => {
    const [, send] = useContext(MachineContext);
    const [activeCategory, setActiveCategory] = useState<ShapeCategory>("Basic");

    const paintShape = (kind: string, close: () => void) => {
        const shape = ShapeFlowObject.default(kind);
        send({ type: ADD_MMO, mmo: shape });
        close();
    };

    const shapes = SHAPES_BY_CATEGORY.get(activeCategory) || [];

    return (
        <Popup
            trigger={
                <div className="div_styles button" title="Add Shape">
                    <label>
                        <ShapeIcon />
                    </label>
                    <span>Shape</span>
                </div>
            }
            closeOnDocumentClick
            closeOnEscape
            modal
            className="shapes"
        >
            {(close: () => void) => (
                <div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                        <h3 style={{ margin: 0, fontSize: 18, color: "var(--foreground)" }}>Shapes</h3>
                        <label
                            onClick={(e) => { e.stopPropagation(); close(); }}
                            style={{ cursor: "pointer", color: "var(--foreground)" }}
                        >
                            <XCircle className="feather" />
                        </label>
                    </div>
                    <div className="shape-modal-tabs">
                        {SHAPE_CATEGORIES.map(cat => (
                            <button
                                key={cat}
                                className={`shape-tab${activeCategory === cat ? " active" : ""}`}
                                onClick={() => setActiveCategory(cat)}
                            >
                                {cat}
                            </button>
                        ))}
                    </div>
                    <div className="shape-modal-grid">
                        {shapes.map(def => (
                            <svg
                                key={def.kind}
                                width={60}
                                height={60}
                                viewBox="0 0 60 60"
                                title={def.label}
                                onClick={() => paintShape(def.kind, close)}
                            >
                                <ShapeThumbnailInner def={def} size={60} />
                            </svg>
                        ))}
                    </div>
                </div>
            )}
        </Popup>
    );
};

/** Inline SVG content (no wrapping <svg>) for use inside the grid's <svg> */
const ShapeThumbnailInner: React.FC<{ def: ShapeDefinition; size: number }> = ({ def, size }) => {
    const pad = 6;
    const inner = size - pad * 2;

    switch (def.renderType) {
        case "rect":
            return <rect className="shape_fill" x={pad} y={pad} width={inner} height={inner} />;
        case "ellipse":
            return <ellipse className="shape_fill" cx={size / 2} cy={size / 2} rx={inner / 2} ry={inner / 2} />;
        case "roundedRect": {
            const r = inner * 5 / 28;
            return <rect className="shape_fill" x={pad} y={pad} width={inner} height={inner} rx={r} ry={r} />;
        }
        case "path": {
            if (def.pathString) {
                const scaled = def.pathString.replace(
                    /(\d+\.?\d*)\s+(\d+\.?\d*)/g,
                    (_m, x, y) => `${parseFloat(x) * inner + pad} ${parseFloat(y) * inner + pad}`
                );
                return <path className="shape_fill" d={scaled} fillRule="evenodd" />;
            }
            return null;
        }
        case "polygon":
        default: {
            const points = def.pointsArrayString
                .split(" ")
                .map(pair => {
                    const [x, y] = pair.split(",");
                    return `${parseFloat(x) * inner + pad},${parseFloat(y) * inner + pad}`;
                })
                .join(" ");
            return <polygon className="shape_fill" points={points} />;
        }
    }
};

export default ShapeMenu;
